import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Switch from "../../../components/Switch/Switch";
import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";
import { DayPicker } from "react-day-picker";
import { FaRegCalendarAlt } from "react-icons/fa";
import "react-day-picker/dist/style.css";
import "./EventForm.css";

const EventForm = ({
  changeHandler,
  onDateRangeChange,
  handleEditorChange,
  submitHandler,
  title,
  description,
  is_holiday,
  event_date,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);

  const toDate = (value) => {
    if (!value) {
      return undefined;
    }
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) {
      return undefined;
    }
    return new Date(year, month - 1, day);
  };

  const startDate = toDate(event_date?.start_date);
  const endDate = toDate(event_date?.end_date);

  const startLabel = startDate
    ? startDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

  const endLabel = endDate
    ? endDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

  const isSingleDayRange = Boolean(
    startDate && endDate && startDate.toDateString() === endDate.toDateString(),
  );

  const dateRangeLabel = isSingleDayRange
    ? startLabel
    : startLabel && endLabel
      ? `${startLabel} to ${endLabel}`
      : startLabel
        ? startLabel
        : "Select event date range";

  const selectedDateRange = {
    from: toDate(event_date?.start_date),
    to: toDate(event_date?.end_date),
  };

  const hasSelectedRange = Boolean(event_date?.start_date || event_date?.end_date);

  const handleDateSelect = (range) => {
    onDateRangeChange(range);
  };

  return (
    <Form className="event-form" onSubmit={submitHandler}>
      <div className="event-form-shell">
        <div className="event-form-section">
          <Input
            label="Title"
            type="text"
            name="title"
            value={title}
            placeholder={"Enter Title"}
            onChangeHandler={changeHandler}
            required={true}
          />
        </div>

        <div className="event-form-section event-date-section">
          <div className="event-section-title">
            <span className="event-title-icon" aria-hidden="true">
              <FaRegCalendarAlt />
            </span>
            <Form.Label className="mb-0 text-capitalize fw-semibold d-block">
              Event Date Range
            </Form.Label>
          </div>

          <div className="event-date-block">
            <button
              type="button"
              className="event-date-trigger"
              onClick={() => setShowCalendar((prev) => !prev)}
              aria-expanded={showCalendar}
            >
              <span className="event-date-label">{dateRangeLabel}</span>
              <span className="event-date-icon" aria-hidden="true">
                <FaRegCalendarAlt />
              </span>
            </button>

            {showCalendar && (
              <div className="event-calendar-panel">
                <DayPicker
                  mode="range"
                  selected={selectedDateRange}
                  onSelect={handleDateSelect}
                  defaultMonth={selectedDateRange.from}
                  numberOfMonths={2}
                />

                <div className="event-calendar-actions">
                  <button
                    type="button"
                    className="event-calendar-clear"
                    onClick={() => onDateRangeChange()}
                    disabled={!hasSelectedRange}
                  >
                    Clear
                  </button>

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    onClick={() => setShowCalendar(false)}
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="event-form-section event-holiday-section">
          <Form.Group>
            <div className="event-section-title mb-2">
              <Form.Label className="mb-0 text-capitalize fw-semibold d-block">
                Holiday Settings
              </Form.Label>
            </div>
            <Switch
              name="is_holiday"
              id="is_holiday"
              label={"Is Holiday"}
              checked={is_holiday.toString() === "1"}
              value={"1"}
              onChange={changeHandler}
            />
          </Form.Group>
        </div>

        <div className="event-form-section event-description-section">
          <Form.Group className="mb-3" controlId="description">
            <Form.Label>Description</Form.Label>
            <CKEditor
              name="description"
              id={"description"}
              editor={ClassicEditor}
              config={{
                toolbar: [
                  "heading",
                  "|",
                  "bold",
                  "italic",
                  "link",
                  "bulletedList",
                  "numberedList",
                  "blockQuote",
                  "|",
                  "undo",
                  "redo",
                ],
              }}
              data={description}
              onChange={handleEditorChange}
            />
          </Form.Group>
        </div>

        <div className="event-form-actions">
          <Button variant="primary" type="submit">
            Save Event
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default EventForm;
